import { useState, useEffect, useRef, useCallback } from 'react';
import { adminApi } from '../../services/api';
import { getSocket, connectSocket, subscribeAudit, unsubscribeAudit, clearAuditBuffer, type TrackerAuditEntry } from '../../services/socket';
import {
  Activity,
  RefreshCw,
  ArrowUpCircle,
  CheckCircle2,
  AlertCircle,
  Package,
  Server,
  Clock,
  Cpu,
  HardDrive,
  Search,
  Database,
  Wifi,
  Radio,
  Mail,
  MessageSquare,
  Bell,
  Calendar,
  MemoryStick,
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  Zap,
  Play,
  Pause,
  Loader2,
  Eye,
  X,
  ArrowUpRight,
  ArrowDownLeft,
  BarChart3,
  ExternalLink,
  TrendingUp,
  Gauge,
  HeartPulse,
  ChevronDown,
} from 'lucide-react';
import clsx from 'clsx';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';

// ============================================================================
// Types
// ============================================================================

interface HealthData {
  server: {
    hostname: string;
    platform: string;
    arch: string;
    osRelease: string;
    uptimeOs: number;
    uptimeProcess: number;
    loadAverage: number[];
    cpu: { model: string; cores: number; usagePercent: number };
    memory: { totalGB: number; usedGB: number; freeGB: number; percent: number };
    disk: { total: number; used: number; free: number; percent: number };
  };
  database: { status: string; latencyMs?: number; activeConnections?: number; databaseSize?: string };
  redis: { status: string; latencyMs?: number; memoryUsed?: string; connectedClients?: number; uptimeSeconds?: number };
  websocket: { status: string; activeConnections: number };
  protocols: Array<{ name: string; displayName: string; port: number; enabled: boolean; status: string; activeConnections: number }>;
  api: { status: string; responseTimeMs: number };
  services: { email: boolean; sms: boolean; push: boolean };
  scheduler: { running: boolean; jobs: Array<{ name: string; schedule: string; description: string }> };
  process: { nodeVersion: string; pid: number; heapUsedMB: number; heapTotalMB: number; rssMB: number; externalMB: number; arrayBuffersMB: number };
  overallStatus: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: string;
}

interface ModuleInfo {
  name: string;
  scope: 'backend' | 'frontend';
  installed: string;
  latest: string;
  needsUpdate: boolean;
}

interface SystemModulesResponse {
  modules: ModuleInfo[];
  system: {
    nodeVersion: string;
    npmVersion: string;
    uptime: number;
    platform: string;
    arch: string;
    memoryUsage: { rss: number; heapUsed: number; heapTotal: number };
  };
}

interface LoadTestResult {
  type: string;
  concurrency: number;
  durationSeconds: number;
  totalRequests: number;
  successCount: number;
  errorCount: number;
  latencies: { avg: number; min: number; max: number; p50: number; p95: number; p99: number };
  throughput: number;
  histogram: Array<{ bucket: string; count: number }>;
  errors: Record<string, number>;
  startedAt: string;
  completedAt: string;
}

const CONCURRENCY_PRESETS = [10, 25, 50, 100, 200, 500];

const TEST_TYPES = [
  { value: 'api' as const, label: 'API HTTP' },
  { value: 'websocket' as const, label: 'WebSocket' },
  { value: 'tracker' as const, label: 'Tracker GPS' },
  { value: 'mixed' as const, label: 'Mixte (70/30)' },
];

// ============================================================================
// Helpers
// ============================================================================

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}j ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function progressColor(percent: number): string {
  if (percent >= 80) return 'bg-red-500';
  if (percent >= 60) return 'bg-orange-500';
  return 'bg-green-500';
}

function progressBg(percent: number): string {
  if (percent >= 80) return 'bg-red-100';
  if (percent >= 60) return 'bg-orange-100';
  return 'bg-green-100';
}

const PIE_COLORS = ['#6366f1', '#a5b4fc', '#f59e0b', '#94a3b8'];

// ============================================================================
// Sub-components
// ============================================================================

function StatusBadge({ status }: { status: 'healthy' | 'degraded' | 'unhealthy' }) {
  if (status === 'healthy') {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700">
        <ShieldCheck className="h-4 w-4" /> Opérationnel
      </span>
    );
  }
  if (status === 'degraded') {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-700">
        <ShieldAlert className="h-4 w-4" /> Dégradé
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-700">
      <ShieldX className="h-4 w-4" /> Hors service
    </span>
  );
}

function ServiceStatusDot({ status }: { status: string }) {
  const color = status === 'healthy' || status === 'running'
    ? 'bg-green-500'
    : status === 'degraded' || status === 'stopped'
      ? 'bg-orange-500'
      : 'bg-red-500';
  return <span className={clsx('inline-block h-2.5 w-2.5 rounded-full', color)} />;
}

function ServiceCard({ icon: Icon, iconColor, iconBg, name, status, detail, subDetail }: {
  icon: React.ComponentType<{ className?: string }>;
  iconColor: string;
  iconBg: string;
  name: string;
  status: string;
  detail: string;
  subDetail?: string;
}) {
  return (
    <div className="glass-card p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={clsx('p-2 rounded-lg', iconBg)}>
            <Icon className={clsx('h-5 w-5', iconColor)} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-gray-900">{name}</p>
              <ServiceStatusDot status={status} />
            </div>
            <p className="text-xs text-gray-500 mt-0.5">{detail}</p>
            {subDetail && <p className="text-xs text-gray-400">{subDetail}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main page
// ============================================================================

type TabType = 'health' | 'scheduler' | 'modules' | 'loadtest' | 'monitoring' | 'audit' | 'sms' | 'healthcheck';

const TABS: { key: TabType; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { key: 'health', label: 'Santé du serveur', icon: Activity },
  { key: 'healthcheck', label: 'Health Check', icon: HeartPulse },
  { key: 'scheduler', label: 'Tâches planifiées', icon: Calendar },
  { key: 'modules', label: 'Modules npm', icon: Package },
  { key: 'loadtest', label: 'Test de charge', icon: Zap },
  { key: 'monitoring', label: 'Monitoring', icon: BarChart3 },
  { key: 'audit', label: 'Audit tracker', icon: Radio },
  { key: 'sms', label: 'Historique SMS', icon: MessageSquare },
];

export default function SystemPage() {
  // --- Tab state ---
  const [activeTab, setActiveTab] = useState<TabType>('health');

  // --- Health state ---
  const [health, setHealth] = useState<HealthData | null>(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [healthError, setHealthError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // --- Modules state ---
  const [modulesData, setModulesData] = useState<SystemModulesResponse | null>(null);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [modulesError, setModulesError] = useState<string | null>(null);
  const [updatingPackages, setUpdatingPackages] = useState<Set<string>>(new Set());
  const [updatingAll, setUpdatingAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [scopeFilter, setScopeFilter] = useState<'all' | 'backend' | 'frontend'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'outdated' | 'uptodate'>('all');
  const [updateResults, setUpdateResults] = useState<Record<string, { success: boolean; message: string }>>({});
  const modulesLoadedRef = useRef(false);

  // --- Load test state ---
  const [loadTestConfig, setLoadTestConfig] = useState<{ type: 'api' | 'websocket' | 'mixed' | 'tracker'; concurrency: number; durationSeconds: number }>({
    type: 'api', concurrency: 10, durationSeconds: 5,
  });
  const [loadTestRunning, setLoadTestRunning] = useState(false);
  const [loadTestResult, setLoadTestResult] = useState<LoadTestResult | null>(null);
  const [loadTestError, setLoadTestError] = useState<string | null>(null);

  // --- Audit state ---
  const [auditEntries, setAuditEntries] = useState<TrackerAuditEntry[]>([]);
  const [auditPaused, setAuditPaused] = useState(false);
  const [auditFilter, setAuditFilter] = useState('');
  const [auditTotalCount, setAuditTotalCount] = useState(0);
  const [auditExpandedRow, setAuditExpandedRow] = useState<number | null>(null);
  const [auditHexModal, setAuditHexModal] = useState<string | null>(null);
  const auditContainerRef = useRef<HTMLDivElement>(null);
  const auditPausedRef = useRef(false);
  const MAX_AUDIT_ENTRIES = 500;

  // --- Truphone SMS history state ---
  const [smsHistory, setSmsHistory] = useState<Array<{
    id: string;
    truphoneId: string;
    date: string;
    content: string;
    iccid: string;
    deliveryStatus: string;
    type: 'command' | 'response';
    device: { id: string; name: string; imei: string; simIccid: string | null } | null;
  }>>([]);
  const [smsHistoryLoading, setSmsHistoryLoading] = useState(false);
  const [smsHistoryTotal, setSmsHistoryTotal] = useState(0);
  const [smsHistoryError, setSmsHistoryError] = useState<string | null>(null);
  const [smsHistoryFilter, setSmsHistoryFilter] = useState<'all' | 'command' | 'response'>('all');
  const [smsHistoryPage, setSmsHistoryPage] = useState(1);
  const SMS_PER_PAGE = 50;
  const smsHistoryLoadedRef = useRef(false);

  // --- Monitoring state ---
  interface MetricsData {
    timestamp: string;
    http: { totalRequests: number; errorRequests: number; errorRate: number; avgResponseTimeMs: number; requestCount: number };
    tcp: {
      activeConnections: Array<{ labels: Record<string, string>; value: number }>;
      totalConnections: Array<{ labels: Record<string, string>; value: number }>;
      messagesReceived: Array<{ labels: Record<string, string>; value: number }>;
      errors: Array<{ labels: Record<string, string>; value: number }>;
    };
    websocket: { activeConnections: number };
    database: { totalQueries: number; avgLatencyMs: number };
    redis: { totalOperations: number; avgLatencyMs: number };
    business: { positionsProcessed: number; devicesOnline: number };
    process: { residentMemoryMB: number; heapUsedMB: number; heapTotalMB: number; cpuUserSeconds: number; cpuSystemSeconds: number; eventLoopLagMs: number };
  }
  const [metricsData, setMetricsData] = useState<MetricsData | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsError, setMetricsError] = useState<string | null>(null);
  const [metricsAutoRefresh, setMetricsAutoRefresh] = useState(true);
  const metricsIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [metricsHistory, setMetricsHistory] = useState<Array<{ time: string; httpReqs: number; tcpMsgs: number; positions: number }>>([]);

  // --- Load health ---
  const loadHealth = useCallback(async (silent = false) => {
    try {
      if (!silent) setHealthLoading(true);
      else setRefreshing(true);
      setHealthError(null);
      const response = await adminApi.getSystemHealth();
      setHealth(response.data);
    } catch {
      setHealthError('Erreur lors du chargement de la santé du serveur');
    } finally {
      setHealthLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadHealth();
  }, [loadHealth]);

  // --- Auto-refresh ---
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => loadHealth(true), 30000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoRefresh, loadHealth]);

  // --- Modules lazy load ---
  const loadModules = useCallback(async () => {
    try {
      setModulesLoading(true);
      setModulesError(null);
      const response = await adminApi.getSystemModules();
      setModulesData(response.data);
      modulesLoadedRef.current = true;
    } catch {
      setModulesError('Erreur lors du chargement des modules');
    } finally {
      setModulesLoading(false);
    }
  }, []);

  // Load modules when switching to modules tab
  useEffect(() => {
    if (activeTab === 'modules' && !modulesLoadedRef.current) {
      loadModules();
    }
  }, [activeTab, loadModules]);

  // --- Truphone SMS history ---
  const loadSmsHistory = useCallback(async () => {
    try {
      setSmsHistoryLoading(true);
      setSmsHistoryError(null);
      const response = await adminApi.getTruphoneSmsHistory();
      setSmsHistory(response.data.data);
      setSmsHistoryTotal(response.data.total);
      smsHistoryLoadedRef.current = true;
    } catch (err: any) {
      setSmsHistoryError(err?.response?.data?.error || 'Erreur lors du chargement');
    } finally {
      setSmsHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'sms' && !smsHistoryLoadedRef.current) {
      loadSmsHistory();
    }
  }, [activeTab, loadSmsHistory]);

  // --- Monitoring load ---
  const loadMetrics = useCallback(async () => {
    try {
      setMetricsLoading(true);
      setMetricsError(null);
      const response = await adminApi.getSystemMetrics();
      const data = response.data;
      setMetricsData(data);
      // Build history point
      const totalTcpMsgs = data.tcp.messagesReceived.reduce((sum, v) => sum + v.value, 0);
      setMetricsHistory((prev) => {
        const time = new Date(data.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
        const next = [...prev, { time, httpReqs: data.http.totalRequests, tcpMsgs: totalTcpMsgs, positions: data.business.positionsProcessed }];
        if (next.length > 30) return next.slice(-30);
        return next;
      });
    } catch {
      setMetricsError('Erreur lors du chargement des métriques');
    } finally {
      setMetricsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'monitoring') {
      loadMetrics();
    }
  }, [activeTab, loadMetrics]);

  useEffect(() => {
    if (activeTab === 'monitoring' && metricsAutoRefresh) {
      metricsIntervalRef.current = setInterval(() => loadMetrics(), 10000);
    }
    return () => {
      if (metricsIntervalRef.current) clearInterval(metricsIntervalRef.current);
    };
  }, [activeTab, metricsAutoRefresh, loadMetrics]);

  const filteredSmsHistory = smsHistoryFilter === 'all'
    ? smsHistory
    : smsHistory.filter(s => s.type === smsHistoryFilter);

  const smsHistoryTotalPages = Math.max(1, Math.ceil(filteredSmsHistory.length / SMS_PER_PAGE));
  const paginatedSmsHistory = filteredSmsHistory.slice((smsHistoryPage - 1) * SMS_PER_PAGE, smsHistoryPage * SMS_PER_PAGE);

  // --- Audit subscription ---
  useEffect(() => {
    auditPausedRef.current = auditPaused;
  }, [auditPaused]);

  useEffect(() => {
    if (activeTab !== 'audit') return;

    // Ensure the socket is connected before subscribing
    let socket = getSocket();
    if (!socket) {
      try {
        socket = connectSocket();
      } catch {
        return;
      }
    }

    subscribeAudit();

    const handler = (data: TrackerAuditEntry) => {
      setAuditTotalCount((c) => c + 1);
      if (auditPausedRef.current) return;
      setAuditEntries((prev) => {
        const next = [...prev, data];
        if (next.length > MAX_AUDIT_ENTRIES) {
          return next.slice(next.length - MAX_AUDIT_ENTRIES);
        }
        return next;
      });
    };

    // Handle buffered history sent on subscription
    const historyHandler = (history: TrackerAuditEntry[]) => {
      if (history && history.length > 0) {
        setAuditTotalCount((c) => c + history.length);
        if (!auditPausedRef.current) {
          setAuditEntries((prev) => {
            const next = [...prev, ...history];
            if (next.length > MAX_AUDIT_ENTRIES) {
              return next.slice(next.length - MAX_AUDIT_ENTRIES);
            }
            return next;
          });
        }
      }
    };

    // Re-subscribe when socket reconnects
    const connectHandler = () => {
      subscribeAudit();
    };

    socket.on('tracker:audit', handler);
    socket.on('tracker:audit:history', historyHandler);
    socket.on('connect', connectHandler);

    return () => {
      socket.off('tracker:audit', handler);
      socket.off('tracker:audit:history', historyHandler);
      socket.off('connect', connectHandler);
      unsubscribeAudit();
    };
  }, [activeTab]);

  // Auto-scroll audit
  useEffect(() => {
    if (!auditPaused && auditContainerRef.current) {
      auditContainerRef.current.scrollTop = auditContainerRef.current.scrollHeight;
    }
  }, [auditEntries, auditPaused]);

  const filteredAuditEntries = auditFilter
    ? auditEntries.filter((e) => e.imei.includes(auditFilter))
    : auditEntries;

  // --- Modules operations ---
  const updatePackage = async (name: string, scope: 'backend' | 'frontend') => {
    const key = `${scope}:${name}`;
    setUpdatingPackages((prev) => new Set(prev).add(key));
    setUpdateResults((prev) => { const next = { ...prev }; delete next[key]; return next; });
    try {
      const response = await adminApi.updateSystemModules({ packages: [{ name, scope }] });
      const result = response.data.results[0];
      setUpdateResults((prev) => ({ ...prev, [key]: { success: result.success, message: result.message } }));
      if (result.success) await loadModules();
    } catch {
      setUpdateResults((prev) => ({ ...prev, [key]: { success: false, message: 'Erreur réseau' } }));
    } finally {
      setUpdatingPackages((prev) => { const next = new Set(prev); next.delete(key); return next; });
    }
  };

  const updateAll = async () => {
    if (!modulesData) return;
    const outdated = modulesData.modules.filter((m) => m.needsUpdate);
    if (outdated.length === 0) return;
    setUpdatingAll(true);
    try {
      const packages = outdated.map((m) => ({ name: m.name, scope: m.scope }));
      const response = await adminApi.updateSystemModules({ packages });
      const resultMap: Record<string, { success: boolean; message: string }> = {};
      for (const r of response.data.results) {
        resultMap[`${r.scope}:${r.name}`] = { success: r.success, message: r.message };
      }
      setUpdateResults((prev) => ({ ...prev, ...resultMap }));
      await loadModules();
    } catch {
      setModulesError('Erreur lors de la mise à jour globale');
    } finally {
      setUpdatingAll(false);
    }
  };

  // --- Load test ---
  const runLoadTest = async () => {
    try {
      setLoadTestRunning(true);
      setLoadTestError(null);
      setLoadTestResult(null);
      const response = await adminApi.runLoadTest(loadTestConfig);
      setLoadTestResult(response.data);
    } catch (err: any) {
      setLoadTestError(err.response?.data?.error || err.message || 'Erreur lors du test de charge');
    } finally {
      setLoadTestRunning(false);
    }
  };

  const filteredModules = modulesData?.modules.filter((m) => {
    if (scopeFilter !== 'all' && m.scope !== scopeFilter) return false;
    if (statusFilter === 'outdated' && !m.needsUpdate) return false;
    if (statusFilter === 'uptodate' && m.needsUpdate) return false;
    if (searchQuery && !m.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  }) || [];

  const outdatedCount = modulesData?.modules.filter((m) => m.needsUpdate).length || 0;

  // --- Pie chart data ---
  const pieData = health ? [
    { name: 'Heap utilisé', value: health.process.heapUsedMB },
    { name: 'Heap libre', value: health.process.heapTotalMB - health.process.heapUsedMB },
    { name: 'Externe', value: health.process.externalMB },
    { name: 'RSS overhead', value: Math.max(0, health.process.rssMB - health.process.heapTotalMB - health.process.externalMB) },
  ] : [];

  // ========================================================================
  // RENDER
  // ========================================================================

  if (healthLoading && !health) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 text-purple-500 animate-spin" />
      </div>
    );
  }

  if (healthError && !health) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <AlertCircle className="h-12 w-12 text-red-400" />
        <p className="text-gray-600">{healthError}</p>
        <button onClick={() => loadHealth()} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ================================================================ */}
      {/* Header */}
      {/* ================================================================ */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-purple-100 rounded-xl">
            <Server className="h-6 w-6 text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Système</h1>
            <p className="text-sm text-gray-500">Surveillance et administration de l'infrastructure</p>
          </div>
          {health && <StatusBadge status={health.overallStatus} />}
        </div>
        {activeTab === 'health' && (
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              Auto (30s)
            </label>
            <button
              onClick={() => loadHealth(true)}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <RefreshCw className={clsx('h-4 w-4', refreshing && 'animate-spin')} />
              Actualiser
            </button>
          </div>
        )}
      </div>

      {/* ================================================================ */}
      {/* Tabs */}
      {/* ================================================================ */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-1 -mb-px overflow-x-auto">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={clsx(
                  'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                  activeTab === tab.key
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                )}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* ================================================================ */}
      {/* Tab: Santé du serveur */}
      {/* ================================================================ */}
      {activeTab === 'health' && health && (
        <div className="space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="glass-card p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Clock className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Uptime processus</p>
                  <p className="text-lg font-bold text-gray-900">{formatUptime(health.server.uptimeProcess)}</p>
                  <p className="text-xs text-gray-400">Système : {formatUptime(health.server.uptimeOs)}</p>
                </div>
              </div>
            </div>
            <div className="glass-card p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Cpu className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500">CPU</p>
                  <p className="text-lg font-bold text-gray-900">{health.server.cpu.usagePercent}%</p>
                  <p className="text-xs text-gray-400">{health.server.cpu.cores} coeurs</p>
                </div>
              </div>
            </div>
            <div className="glass-card p-4">
              <div className="flex items-center gap-3 w-full">
                <div className="p-2 bg-green-100 rounded-lg shrink-0">
                  <MemoryStick className="h-5 w-5 text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500">RAM</p>
                  <p className="text-lg font-bold text-gray-900">{health.server.memory.usedGB} / {health.server.memory.totalGB} Go</p>
                  <div className={clsx('w-full h-1.5 rounded-full mt-1', progressBg(health.server.memory.percent))}>
                    <div className={clsx('h-full rounded-full transition-all', progressColor(health.server.memory.percent))} style={{ width: `${health.server.memory.percent}%` }} />
                  </div>
                </div>
              </div>
            </div>
            <div className="glass-card p-4">
              <div className="flex items-center gap-3 w-full">
                <div className="p-2 bg-orange-100 rounded-lg shrink-0">
                  <HardDrive className="h-5 w-5 text-orange-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500">Disque</p>
                  <p className="text-lg font-bold text-gray-900">{health.server.disk.used} / {health.server.disk.total} Go</p>
                  <div className={clsx('w-full h-1.5 rounded-full mt-1', progressBg(health.server.disk.percent))}>
                    <div className={clsx('h-full rounded-full transition-all', progressColor(health.server.disk.percent))} style={{ width: `${health.server.disk.percent}%` }} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Services grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <ServiceCard
              icon={Database}
              iconColor="text-indigo-600"
              iconBg="bg-indigo-100"
              name="Base de données"
              status={health.database.status}
              detail={health.database.status === 'healthy'
                ? `${health.database.latencyMs}ms · ${health.database.databaseSize}`
                : 'Hors service'}
              subDetail={health.database.activeConnections !== undefined ? `${health.database.activeConnections} connexions actives` : undefined}
            />
            <ServiceCard
              icon={Server}
              iconColor="text-red-600"
              iconBg="bg-red-100"
              name="Redis"
              status={health.redis.status}
              detail={health.redis.status === 'healthy'
                ? `${health.redis.latencyMs}ms · ${health.redis.memoryUsed}`
                : 'Hors service'}
              subDetail={health.redis.connectedClients !== undefined ? `${health.redis.connectedClients} clients` : undefined}
            />
            <ServiceCard
              icon={Activity}
              iconColor="text-green-600"
              iconBg="bg-green-100"
              name="API"
              status={health.api.status}
              detail={`${health.api.responseTimeMs}ms`}
            />
            <ServiceCard
              icon={Wifi}
              iconColor="text-blue-600"
              iconBg="bg-blue-100"
              name="WebSocket"
              status={health.websocket.status}
              detail={`${health.websocket.activeConnections} connexions`}
            />
            {health.protocols.map((p) => (
              <ServiceCard
                key={p.name}
                icon={Radio}
                iconColor="text-purple-600"
                iconBg="bg-purple-100"
                name={`TCP ${p.displayName}`}
                status={p.status}
                detail={`Port ${p.port}`}
                subDetail={`${p.activeConnections} connexions`}
              />
            ))}
            <ServiceCard
              icon={Mail}
              iconColor="text-amber-600"
              iconBg="bg-amber-100"
              name="Email (SMTP)"
              status={health.services.email ? 'healthy' : 'stopped'}
              detail={health.services.email ? 'Configuré' : 'Non configuré'}
            />
            <ServiceCard
              icon={MessageSquare}
              iconColor="text-teal-600"
              iconBg="bg-teal-100"
              name="SMS (Twilio)"
              status={health.services.sms ? 'healthy' : 'stopped'}
              detail={health.services.sms ? 'Configuré' : 'Non configuré'}
            />
            <ServiceCard
              icon={Bell}
              iconColor="text-pink-600"
              iconBg="bg-pink-100"
              name="Push (Firebase)"
              status={health.services.push ? 'healthy' : 'stopped'}
              detail={health.services.push ? 'Configuré' : 'Non configuré'}
            />
          </div>

          {/* Charts + Process Info */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="glass-card p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Répartition mémoire processus</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {pieData.map((_entry, index) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `${value} Mo`} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="glass-card p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Informations processus</h3>
              <div className="space-y-3">
                {[
                  { label: 'Node.js', value: health.process.nodeVersion },
                  { label: 'PID', value: String(health.process.pid) },
                  { label: 'Plateforme', value: `${health.server.platform} / ${health.server.arch}` },
                  { label: 'Hostname', value: health.server.hostname },
                  { label: 'OS', value: health.server.osRelease },
                  { label: 'Charge (1/5/15 min)', value: health.server.loadAverage.map((l) => l.toFixed(2)).join(' / ') },
                  { label: 'RSS', value: `${health.process.rssMB} Mo` },
                  { label: 'Heap', value: `${health.process.heapUsedMB} / ${health.process.heapTotalMB} Mo` },
                  { label: 'Externe', value: `${health.process.externalMB} Mo` },
                  { label: 'ArrayBuffers', value: `${health.process.arrayBuffersMB} Mo` },
                ].map(({ label, value }) => (
                  <div key={label} className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">{label}</span>
                    <span className="font-mono text-gray-900">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================================================================ */}
      {/* Tab: Tâches planifiées */}
      {/* ================================================================ */}
      {activeTab === 'scheduler' && health && (
        <div className="glass-card overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-900">Tâches planifiées</h3>
            </div>
            <span className={clsx(
              'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
              health.scheduler.running ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700',
            )}>
              {health.scheduler.running ? (
                <><CheckCircle2 className="h-3 w-3" /> Actif</>
              ) : (
                <><AlertCircle className="h-3 w-3" /> Arrêté</>
              )}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/50">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Tâche</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Planification</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {health.scheduler.jobs.map((job) => (
                  <tr key={job.name} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-2.5 text-sm font-medium text-gray-900">{job.name}</td>
                    <td className="px-4 py-2.5 text-sm font-mono text-gray-600">{job.schedule}</td>
                    <td className="px-4 py-2.5 text-sm text-gray-500">{job.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ================================================================ */}
      {/* Tab: Modules npm */}
      {/* ================================================================ */}
      {activeTab === 'modules' && (
        <div className="glass-card overflow-hidden">
          {modulesLoading && !modulesData ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 text-purple-500 animate-spin" />
            </div>
          ) : modulesError && !modulesData ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <AlertCircle className="h-8 w-8 text-red-400" />
              <p className="text-sm text-gray-600">{modulesError}</p>
              <button onClick={loadModules} className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
                Réessayer
              </button>
            </div>
          ) : (
            <>
              {/* Action bar */}
              <div className="px-4 py-3 border-b border-gray-200 flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
                <div className="flex flex-col sm:flex-row gap-3 flex-1 w-full sm:w-auto">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Rechercher un module..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500/20"
                    />
                  </div>
                  <select
                    value={scopeFilter}
                    onChange={(e) => setScopeFilter(e.target.value as 'all' | 'backend' | 'frontend')}
                    className="px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-700 focus:outline-none focus:border-purple-500"
                  >
                    <option value="all">Tous les scopes</option>
                    <option value="backend">Backend</option>
                    <option value="frontend">Frontend</option>
                  </select>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as 'all' | 'outdated' | 'uptodate')}
                    className="px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-700 focus:outline-none focus:border-purple-500"
                  >
                    <option value="all">Tous les statuts</option>
                    <option value="outdated">Mise à jour disponible</option>
                    <option value="uptodate">À jour</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  {outdatedCount > 0 && (
                    <button
                      onClick={updateAll}
                      disabled={updatingAll}
                      className="flex items-center gap-2 px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 transition-colors text-sm"
                    >
                      {updatingAll ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <ArrowUpCircle className="h-3.5 w-3.5" />}
                      Tout mettre à jour ({outdatedCount})
                    </button>
                  )}
                  <button
                    onClick={loadModules}
                    disabled={modulesLoading}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                  >
                    <RefreshCw className={clsx('h-3.5 w-3.5', modulesLoading && 'animate-spin')} />
                    Actualiser
                  </button>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50/50">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Module</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Scope</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Version installée</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Dernière version</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Statut</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredModules.map((mod) => {
                      const key = `${mod.scope}:${mod.name}`;
                      const isUpdating = updatingPackages.has(key);
                      const result = updateResults[key];
                      return (
                        <tr key={key} className="hover:bg-gray-50/50 transition-colors">
                          <td className="px-4 py-3"><span className="text-sm font-medium text-gray-900">{mod.name}</span></td>
                          <td className="px-4 py-3">
                            <span className={clsx(
                              'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                              mod.scope === 'backend' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700',
                            )}>
                              {mod.scope}
                            </span>
                          </td>
                          <td className="px-4 py-3"><span className="text-sm text-gray-600 font-mono">{mod.installed}</span></td>
                          <td className="px-4 py-3"><span className="text-sm text-gray-600 font-mono">{mod.latest}</span></td>
                          <td className="px-4 py-3">
                            {mod.needsUpdate ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                                <ArrowUpCircle className="h-3 w-3" /> Mise à jour disponible
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                <CheckCircle2 className="h-3 w-3" /> À jour
                              </span>
                            )}
                            {result && (
                              <span className={clsx(
                                'ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
                                result.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700',
                              )}>
                                {result.success ? 'Mis à jour' : 'Échec'}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {mod.needsUpdate && (
                              <button
                                onClick={() => updatePackage(mod.name, mod.scope)}
                                disabled={isUpdating || updatingAll}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                              >
                                {isUpdating ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <ArrowUpCircle className="h-3.5 w-3.5" />}
                                Mettre à jour
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {filteredModules.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                          Aucun module trouvé
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-3 border-t border-gray-200 bg-gray-50/50 text-xs text-gray-500">
                {filteredModules.length} module{filteredModules.length > 1 ? 's' : ''} affiché{filteredModules.length > 1 ? 's' : ''}
                {modulesData && ` sur ${modulesData.modules.length} au total`}
                {outdatedCount > 0 && ` · ${outdatedCount} mise${outdatedCount > 1 ? 's' : ''} à jour disponible${outdatedCount > 1 ? 's' : ''}`}
              </div>
            </>
          )}
        </div>
      )}

      {/* ================================================================ */}
      {/* Tab: Test de charge */}
      {/* ================================================================ */}
      {activeTab === 'loadtest' && (
        <div className="space-y-6">
          {/* Config form */}
          <div className="glass-card p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Type de test</label>
                <div className="flex flex-wrap gap-2">
                  {TEST_TYPES.map((t) => (
                    <button
                      key={t.value}
                      onClick={() => setLoadTestConfig((c) => ({ ...c, type: t.value }))}
                      disabled={loadTestRunning}
                      className={clsx(
                        'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                        loadTestConfig.type === t.value
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
                      )}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Connexions simultanées</label>
                <div className="flex flex-wrap gap-2">
                  {CONCURRENCY_PRESETS.map((n) => (
                    <button
                      key={n}
                      onClick={() => setLoadTestConfig((c) => ({ ...c, concurrency: n }))}
                      disabled={loadTestRunning}
                      className={clsx(
                        'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                        loadTestConfig.concurrency === n
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
                      )}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Durée : {loadTestConfig.durationSeconds}s
                </label>
                <input
                  type="range"
                  min={1}
                  max={30}
                  value={loadTestConfig.durationSeconds}
                  onChange={(e) => setLoadTestConfig((c) => ({ ...c, durationSeconds: parseInt(e.target.value) }))}
                  disabled={loadTestRunning}
                  className="w-full accent-purple-600"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>1s</span>
                  <span>15s</span>
                  <span>30s</span>
                </div>
              </div>
            </div>
          </div>

          {/* Launch button */}
          <div className="flex items-center gap-4">
            <button
              onClick={runLoadTest}
              disabled={loadTestRunning}
              className="flex items-center gap-2 px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors font-medium"
            >
              {loadTestRunning ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Test en cours...</>
              ) : (
                <><Play className="h-4 w-4" /> Lancer le test</>
              )}
            </button>
            {loadTestError && (
              <p className="text-sm text-red-600">{loadTestError}</p>
            )}
          </div>

          {/* Results */}
          {loadTestResult && (
            <>
              {/* KPI cards */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {[
                  { label: 'Total requêtes', value: loadTestResult.totalRequests.toLocaleString() },
                  { label: 'Succès', value: loadTestResult.successCount.toLocaleString(), color: 'text-green-600' },
                  { label: 'Erreurs', value: loadTestResult.errorCount.toLocaleString(), color: loadTestResult.errorCount > 0 ? 'text-red-600' : 'text-gray-900' },
                  { label: 'Débit (req/s)', value: loadTestResult.throughput.toLocaleString() },
                  { label: 'Latence moy.', value: `${loadTestResult.latencies.avg}ms` },
                  { label: 'P95', value: `${loadTestResult.latencies.p95}ms` },
                ].map((kpi) => (
                  <div key={kpi.label} className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500">{kpi.label}</p>
                    <p className={clsx('text-lg font-bold', kpi.color || 'text-gray-900')}>{kpi.value}</p>
                  </div>
                ))}
              </div>

              {/* Latency details table */}
              <div className="glass-card overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200">
                  <h4 className="text-sm font-semibold text-gray-900">Latences détaillées</h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50/50">
                        {['Min', 'Moy', 'P50', 'P95', 'P99', 'Max'].map((h) => (
                          <th key={h} className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {[
                          loadTestResult.latencies.min,
                          loadTestResult.latencies.avg,
                          loadTestResult.latencies.p50,
                          loadTestResult.latencies.p95,
                          loadTestResult.latencies.p99,
                          loadTestResult.latencies.max,
                        ].map((v, i) => (
                          <td key={i} className="text-center px-4 py-3 text-sm font-mono text-gray-900">{v}ms</td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Histogram */}
              <div className="glass-card p-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Distribution des latences</h4>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={loadTestResult.histogram}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#7c3aed" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Errors detail */}
              {loadTestResult.errorCount > 0 && Object.keys(loadTestResult.errors).length > 0 && (
                <div className="glass-card overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-200">
                    <h4 className="text-sm font-semibold text-red-600">Détail des erreurs</h4>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50/50">
                          <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Erreur</th>
                          <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Nombre</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {Object.entries(loadTestResult.errors).map(([key, count]) => (
                          <tr key={key}>
                            <td className="px-4 py-2.5 text-sm text-gray-700 font-mono">{key}</td>
                            <td className="px-4 py-2.5 text-sm text-red-600 font-mono text-right">{count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Metadata footer */}
              <div className="flex flex-wrap gap-4 text-xs text-gray-400">
                <span>Type : {loadTestResult.type}</span>
                <span>Concurrence : {loadTestResult.concurrency}</span>
                <span>Durée : {loadTestResult.durationSeconds}s</span>
                <span>Fin : {new Date(loadTestResult.completedAt).toLocaleString()}</span>
              </div>
            </>
          )}
        </div>
      )}

      {/* ================================================================ */}
      {/* Tab: Monitoring (Prometheus) */}
      {/* ================================================================ */}
      {activeTab === 'monitoring' && (
        <div className="space-y-6">
          {/* Header controls */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <p className="text-sm text-gray-500">
                Métriques Prometheus en temps réel
              </p>
              {metricsData && (
                <span className="text-xs text-gray-400">
                  Dernière mise à jour : {new Date(metricsData.timestamp).toLocaleTimeString('fr-FR')}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={metricsAutoRefresh}
                  onChange={(e) => setMetricsAutoRefresh(e.target.checked)}
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                Auto (10s)
              </label>
              <button
                onClick={loadMetrics}
                disabled={metricsLoading}
                className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
              >
                <RefreshCw className={clsx('h-4 w-4', metricsLoading && 'animate-spin')} />
                Actualiser
              </button>
            </div>
          </div>

          {metricsError && !metricsData && (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <AlertCircle className="h-8 w-8 text-red-400" />
              <p className="text-sm text-gray-600">{metricsError}</p>
              <button onClick={loadMetrics} className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
                Réessayer
              </button>
            </div>
          )}

          {metricsLoading && !metricsData && (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 text-purple-500 animate-spin" />
            </div>
          )}

          {metricsData && (
            <>
              {/* KPI Cards - Row 1: HTTP + Business */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <div className="glass-card p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="h-4 w-4 text-blue-500" />
                    <p className="text-xs text-gray-500">Requêtes HTTP</p>
                  </div>
                  <p className="text-lg font-bold text-gray-900">{metricsData.http.totalRequests.toLocaleString()}</p>
                </div>
                <div className="glass-card p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertCircle className="h-4 w-4 text-red-500" />
                    <p className="text-xs text-gray-500">Taux d'erreur 5xx</p>
                  </div>
                  <p className={clsx('text-lg font-bold', metricsData.http.errorRate > 5 ? 'text-red-600' : metricsData.http.errorRate > 1 ? 'text-orange-600' : 'text-green-600')}>
                    {metricsData.http.errorRate}%
                  </p>
                </div>
                <div className="glass-card p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="h-4 w-4 text-purple-500" />
                    <p className="text-xs text-gray-500">Latence HTTP moy.</p>
                  </div>
                  <p className="text-lg font-bold text-gray-900">{metricsData.http.avgResponseTimeMs}ms</p>
                </div>
                <div className="glass-card p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Gauge className="h-4 w-4 text-green-500" />
                    <p className="text-xs text-gray-500">Appareils en ligne</p>
                  </div>
                  <p className="text-lg font-bold text-gray-900">{metricsData.business.devicesOnline}</p>
                </div>
                <div className="glass-card p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Radio className="h-4 w-4 text-indigo-500" />
                    <p className="text-xs text-gray-500">Positions traitées</p>
                  </div>
                  <p className="text-lg font-bold text-gray-900">{metricsData.business.positionsProcessed.toLocaleString()}</p>
                </div>
                <div className="glass-card p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Wifi className="h-4 w-4 text-teal-500" />
                    <p className="text-xs text-gray-500">WebSocket</p>
                  </div>
                  <p className="text-lg font-bold text-gray-900">{metricsData.websocket.activeConnections}</p>
                </div>
              </div>

              {/* Row 2: TCP + DB + Redis + Process */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* TCP Protocols */}
                <div className="glass-card p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Radio className="h-4 w-4 text-purple-500" />
                    Connexions TCP par protocole
                  </h3>
                  {metricsData.tcp.activeConnections.length === 0 ? (
                    <p className="text-sm text-gray-400">Aucune connexion TCP active</p>
                  ) : (
                    <div className="space-y-3">
                      {metricsData.tcp.activeConnections.map((conn) => {
                        const protocol = conn.labels.protocol || 'inconnu';
                        const totalConns = metricsData.tcp.totalConnections.find(c => c.labels.protocol === protocol)?.value || 0;
                        const totalMsgs = metricsData.tcp.messagesReceived.find(c => c.labels.protocol === protocol)?.value || 0;
                        const totalErrors = metricsData.tcp.errors.find(c => c.labels.protocol === protocol)?.value || 0;
                        return (
                          <div key={protocol} className="p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-gray-900 capitalize">{protocol}</span>
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                {conn.value} active{conn.value > 1 ? 's' : ''}
                              </span>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-xs text-gray-500">
                              <div>Total : <span className="font-mono text-gray-700">{totalConns.toLocaleString()}</span></div>
                              <div>Messages : <span className="font-mono text-gray-700">{totalMsgs.toLocaleString()}</span></div>
                              <div>Erreurs : <span className={clsx('font-mono', totalErrors > 0 ? 'text-red-600' : 'text-gray-700')}>{totalErrors.toLocaleString()}</span></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* DB + Redis + Process */}
                <div className="space-y-4">
                  <div className="glass-card p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Database className="h-4 w-4 text-indigo-500" />
                      Base de données & Redis
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">DB - Requêtes totales</p>
                        <p className="text-lg font-bold text-gray-900">{metricsData.database.totalQueries.toLocaleString()}</p>
                        <p className="text-xs text-gray-400">Latence moy. : {metricsData.database.avgLatencyMs}ms</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">Redis - Opérations</p>
                        <p className="text-lg font-bold text-gray-900">{metricsData.redis.totalOperations.toLocaleString()}</p>
                        <p className="text-xs text-gray-400">Latence moy. : {metricsData.redis.avgLatencyMs}ms</p>
                      </div>
                    </div>
                  </div>

                  <div className="glass-card p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Cpu className="h-4 w-4 text-blue-500" />
                      Processus Node.js
                    </h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">RSS</span>
                        <span className="font-mono text-gray-900">{metricsData.process.residentMemoryMB} Mo</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">Heap</span>
                        <span className="font-mono text-gray-900">{metricsData.process.heapUsedMB} / {metricsData.process.heapTotalMB} Mo</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">CPU user</span>
                        <span className="font-mono text-gray-900">{metricsData.process.cpuUserSeconds}s</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">CPU system</span>
                        <span className="font-mono text-gray-900">{metricsData.process.cpuSystemSeconds}s</span>
                      </div>
                      <div className="flex items-center justify-between col-span-2">
                        <span className="text-gray-500">Event Loop Lag</span>
                        <span className={clsx('font-mono', metricsData.process.eventLoopLagMs > 100 ? 'text-red-600' : metricsData.process.eventLoopLagMs > 50 ? 'text-orange-600' : 'text-green-600')}>
                          {metricsData.process.eventLoopLagMs}ms
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Activity chart */}
              {metricsHistory.length > 1 && (
                <div className="glass-card p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Activité dans le temps</h3>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={metricsHistory}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="time" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="httpReqs" name="HTTP" fill="#6366f1" radius={[2, 2, 0, 0]} />
                        <Bar dataKey="tcpMsgs" name="TCP msgs" fill="#8b5cf6" radius={[2, 2, 0, 0]} />
                        <Bar dataKey="positions" name="Positions" fill="#a78bfa" radius={[2, 2, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Grafana links */}
              <div className="glass-card p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-orange-500" />
                  Dashboards Grafana
                </h3>
                <p className="text-xs text-gray-500 mb-3">
                  Dashboards avancés Grafana (nécessite <code className="bg-gray-100 px-1 rounded">docker-compose.monitoring.yml</code>)
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    { name: 'Vue système', desc: 'CPU, mémoire, uptime, event loop', uid: 'trackfleet-system-overview' },
                    { name: 'Serveur TCP', desc: 'Connexions, messages, erreurs par protocole', uid: 'trackfleet-tcp-server' },
                    { name: 'Performance API', desc: 'Latences HTTP, débit, taux d\'erreur', uid: 'trackfleet-api-performance' },
                  ].map((d) => (
                    <a
                      key={d.uid}
                      href={`/grafana/d/${d.uid}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-purple-50 transition-colors group"
                    >
                      <div className="p-2 bg-orange-100 rounded-lg group-hover:bg-orange-200 transition-colors">
                        <BarChart3 className="h-4 w-4 text-orange-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 group-hover:text-purple-700">{d.name}</p>
                        <p className="text-xs text-gray-500 truncate">{d.desc}</p>
                      </div>
                      <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-purple-500" />
                    </a>
                  ))}
                </div>
              </div>

              {/* Prometheus raw endpoint link */}
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <span>Endpoint Prometheus brut :</span>
                <a
                  href="/metrics"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-500 hover:text-purple-700 underline"
                >
                  /metrics
                </a>
              </div>
            </>
          )}
        </div>
      )}

      {/* ================================================================ */}
      {/* Tab: Audit tracker */}
      {/* ================================================================ */}
      {activeTab === 'audit' && (
        <div className="space-y-4">
          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="relative flex-1 w-full sm:w-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Filtrer par IMEI..."
                value={auditFilter}
                onChange={(e) => setAuditFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500/20 font-mono"
              />
            </div>
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                <Radio className="h-3 w-3" />
                {auditTotalCount} paquets
              </span>
              <span className="text-xs text-gray-400">
                {filteredAuditEntries.length} affiché{filteredAuditEntries.length > 1 ? 's' : ''}
              </span>
              <button
                onClick={() => setAuditPaused((p) => !p)}
                className={clsx(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                  auditPaused
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                )}
              >
                {auditPaused ? (
                  <><Play className="h-3.5 w-3.5" /> Reprendre</>
                ) : (
                  <><Pause className="h-3.5 w-3.5" /> Pause</>
                )}
              </button>
              <button
                onClick={() => { setAuditEntries([]); setAuditTotalCount(0); setAuditExpandedRow(null); clearAuditBuffer(); }}
                className="px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
              >
                Effacer
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="glass-card overflow-hidden">
            <div ref={auditContainerRef} className="overflow-auto max-h-[65vh]">
              <table className="w-full text-xs">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-2 py-2 font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Heure</th>
                    <th className="text-left px-2 py-2 font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">IMEI</th>
                    <th className="text-left px-2 py-2 font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Proto</th>
                    <th className="text-left px-2 py-2 font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">IP</th>
                    <th className="text-left px-2 py-2 font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Codec</th>
                    <th className="text-right px-2 py-2 font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Rec</th>
                    <th className="text-left px-2 py-2 font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">GPS</th>
                    <th className="text-right px-2 py-2 font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">km/h</th>
                    <th className="text-right px-2 py-2 font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Cap</th>
                    <th className="text-center px-2 py-2 font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">IGN</th>
                    <th className="text-right px-2 py-2 font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Ext V</th>
                    <th className="text-right px-2 py-2 font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Batt V</th>
                    <th className="text-right px-2 py-2 font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Sat</th>
                    <th className="text-left px-2 py-2 font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">HEX brut</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredAuditEntries.length === 0 ? (
                    <tr>
                      <td colSpan={14} className="px-4 py-12 text-center text-sm text-gray-400">
                        <Radio className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                        <p>En attente de données des trackers...</p>
                        <p className="text-xs mt-1">Les paquets apparaîtront ici en temps réel</p>
                      </td>
                    </tr>
                  ) : (
                    filteredAuditEntries.map((entry, idx) => {
                      const pos = entry.positions[0];
                      const time = new Date(entry.timestamp);
                      const timeStr = time.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) + '.' + String(time.getMilliseconds()).padStart(3, '0');
                      const extV = pos?.externalVoltage != null ? (Number(pos.externalVoltage) / 1000).toFixed(1) : '-';
                      const battV = pos?.batteryVoltage != null ? (Number(pos.batteryVoltage) / 1000).toFixed(2) : '-';
                      const codecStr = entry.codecId != null ? `0x${entry.codecId.toString(16).toUpperCase().padStart(2, '0')}` : '-';
                      const hexTruncated = entry.rawHex ? (entry.rawHex.length > 60 ? entry.rawHex.substring(0, 60) + '...' : entry.rawHex) : '-';
                      const isExpanded = auditExpandedRow === idx;

                      return (
                        <tr
                          key={idx}
                          onClick={() => setAuditExpandedRow(isExpanded ? null : idx)}
                          className={clsx(
                            'hover:bg-purple-50/50 transition-colors cursor-pointer',
                            isExpanded && 'bg-purple-50/30'
                          )}
                        >
                          <td className="px-2 py-1.5 font-mono text-gray-600 whitespace-nowrap">{timeStr}</td>
                          <td className="px-2 py-1.5 font-mono font-medium text-gray-900 whitespace-nowrap">{entry.imei}</td>
                          <td className="px-2 py-1.5 text-gray-600 whitespace-nowrap">{entry.protocol}</td>
                          <td className="px-2 py-1.5 font-mono text-gray-500 whitespace-nowrap">{entry.remoteAddress || '-'}</td>
                          <td className="px-2 py-1.5 font-mono text-gray-600 whitespace-nowrap">{codecStr}</td>
                          <td className="px-2 py-1.5 text-right font-mono text-gray-600">{entry.recordCount}</td>
                          <td className="px-2 py-1.5 font-mono text-gray-600 whitespace-nowrap">
                            {pos ? `${pos.latitude.toFixed(5)}, ${pos.longitude.toFixed(5)}` : '-'}
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono text-gray-600">{pos?.speed ?? '-'}</td>
                          <td className="px-2 py-1.5 text-right font-mono text-gray-600">{pos?.heading ?? '-'}</td>
                          <td className="px-2 py-1.5 text-center">
                            {pos?.ignition != null ? (
                              <span className={clsx(
                                'inline-block h-2 w-2 rounded-full',
                                pos.ignition ? 'bg-green-500' : 'bg-red-400'
                              )} title={pos.ignition ? 'Oui' : 'Non'} />
                            ) : '-'}
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono text-gray-600">{extV}</td>
                          <td className="px-2 py-1.5 text-right font-mono text-gray-600">{battV}</td>
                          <td className="px-2 py-1.5 text-right font-mono text-gray-600">{pos?.satellites ?? '-'}</td>
                          <td className="px-2 py-1.5 font-mono text-gray-400 whitespace-nowrap">
                            <div className="flex items-center gap-1">
                              <span className="truncate max-w-[200px]">{hexTruncated}</span>
                              {entry.rawHex && entry.rawHex.length > 60 && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); setAuditHexModal(entry.rawHex!); }}
                                  className="p-0.5 text-purple-500 hover:text-purple-700"
                                  title="Voir le HEX complet"
                                >
                                  <Eye className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Expanded row detail */}
          {auditExpandedRow !== null && filteredAuditEntries[auditExpandedRow] && (() => {
            const entry = filteredAuditEntries[auditExpandedRow];
            return (
              <div className="glass-card p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-gray-900">
                    Détail du paquet - IMEI {entry.imei}
                  </h4>
                  <button onClick={() => setAuditExpandedRow(null)} className="p-1 text-gray-400 hover:text-gray-600">
                    <X className="h-4 w-4" />
                  </button>
                </div>
                {entry.rawHex && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">HEX complet</p>
                    <div className="p-2 bg-gray-900 rounded-lg overflow-x-auto">
                      <pre className="text-xs text-green-400 font-mono break-all whitespace-pre-wrap">{entry.rawHex}</pre>
                    </div>
                  </div>
                )}
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Positions ({entry.positions.length})</p>
                  <div className="space-y-2">
                    {entry.positions.map((pos, pi) => (
                      <div key={pi} className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-2 bg-gray-50 rounded-lg text-xs">
                        <div><span className="text-gray-400">Lat:</span> <span className="font-mono">{pos.latitude.toFixed(6)}</span></div>
                        <div><span className="text-gray-400">Lng:</span> <span className="font-mono">{pos.longitude.toFixed(6)}</span></div>
                        <div><span className="text-gray-400">Vitesse:</span> <span className="font-mono">{pos.speed} km/h</span></div>
                        <div><span className="text-gray-400">Cap:</span> <span className="font-mono">{pos.heading}°</span></div>
                        <div><span className="text-gray-400">Altitude:</span> <span className="font-mono">{pos.altitude} m</span></div>
                        <div><span className="text-gray-400">Satellites:</span> <span className="font-mono">{pos.satellites}</span></div>
                        <div><span className="text-gray-400">Ignition:</span> <span className="font-mono">{pos.ignition != null ? (pos.ignition ? 'Oui' : 'Non') : '-'}</span></div>
                        <div><span className="text-gray-400">Horodatage:</span> <span className="font-mono">{new Date(pos.timestamp).toLocaleString('fr-FR')}</span></div>
                        <div><span className="text-gray-400">Tension ext:</span> <span className="font-mono">{pos.externalVoltage != null ? `${(Number(pos.externalVoltage) / 1000).toFixed(2)} V` : '-'}</span></div>
                        <div><span className="text-gray-400">Batterie:</span> <span className="font-mono">{pos.batteryVoltage != null ? `${(Number(pos.batteryVoltage) / 1000).toFixed(3)} V` : '-'}</span></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* HEX modal */}
          {auditHexModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="fixed inset-0 bg-black/50" onClick={() => setAuditHexModal(null)} />
              <div className="relative bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden">
                <div className="px-6 py-4 border-b flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900">HEX brut complet</h3>
                  <button onClick={() => setAuditHexModal(null)} className="p-1 text-gray-400 hover:text-gray-600">
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="p-6 overflow-auto max-h-[60vh]">
                  <div className="p-3 bg-gray-900 rounded-lg">
                    <pre className="text-xs text-green-400 font-mono break-all whitespace-pre-wrap">{auditHexModal}</pre>
                  </div>
                </div>
                <div className="px-6 py-3 border-t bg-gray-50 flex justify-between items-center">
                  <span className="text-xs text-gray-400">{auditHexModal.length} caractères ({auditHexModal.length / 2} octets)</span>
                  <button
                    onClick={() => { navigator.clipboard.writeText(auditHexModal); }}
                    className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Copier
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ================================================================ */}
      {/* Tab: SMS History (Truphone) */}
      {/* ================================================================ */}
      {activeTab === 'sms' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <p className="text-sm text-gray-500">
                {filteredSmsHistory.length} SMS{filteredSmsHistory.length !== smsHistoryTotal ? ` / ${smsHistoryTotal} total` : ''}
              </p>
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
                {([['all', 'Tous'], ['command', 'Commandes'], ['response', 'Réponses']] as const).map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => { setSmsHistoryFilter(key); setSmsHistoryPage(1); }}
                    className={clsx(
                      'px-3 py-1 rounded-md text-xs font-medium transition-colors',
                      smsHistoryFilter === key
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <button
              onClick={() => { smsHistoryLoadedRef.current = false; loadSmsHistory(); }}
              disabled={smsHistoryLoading}
              className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
            >
              <RefreshCw className={clsx('h-4 w-4', smsHistoryLoading && 'animate-spin')} />
              Actualiser
            </button>
          </div>

          {smsHistoryError && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">
              {smsHistoryError}
            </div>
          )}

          <div className="glass-card overflow-hidden">
            {smsHistoryLoading && smsHistory.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : filteredSmsHistory.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 gap-2">
                <MessageSquare className="h-8 w-8 text-gray-300" />
                <p className="text-sm text-gray-500">Aucun SMS dans l&apos;historique Truphone</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                    <tr>
                      <th className="px-4 py-3 text-left w-10">Type</th>
                      <th className="px-4 py-3 text-left">Device</th>
                      <th className="px-4 py-3 text-left">ICCID</th>
                      <th className="px-4 py-3 text-left">Date/Heure</th>
                      <th className="px-4 py-3 text-left">Message</th>
                      <th className="px-4 py-3 text-left">Livraison</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {paginatedSmsHistory.map((sms) => (
                      <tr key={sms.id} className={clsx(
                        'hover:bg-gray-50',
                        sms.type === 'response' && 'bg-blue-50/30'
                      )}>
                        <td className="px-4 py-3">
                          {sms.type === 'command' ? (
                            <span className="inline-flex items-center gap-1 text-orange-600" title="Commande envoyée">
                              <ArrowUpRight className="h-4 w-4" />
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-blue-600" title="Réponse tracker">
                              <ArrowDownLeft className="h-4 w-4" />
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-medium text-gray-900">
                            {sms.device?.name || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <code className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                            {sms.iccid}
                          </code>
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                          {new Date(sms.date).toLocaleString('fr-FR')}
                        </td>
                        <td className="px-4 py-3">
                          <code className={clsx(
                            'text-xs px-2 py-0.5 rounded block max-w-lg',
                            sms.type === 'command'
                              ? 'bg-orange-50 text-orange-800'
                              : 'bg-blue-50 text-blue-800'
                          )}>
                            {sms.content}
                          </code>
                        </td>
                        <td className="px-4 py-3">
                          <span className={clsx(
                            'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                            sms.deliveryStatus === 'DELIVERED' && 'bg-green-100 text-green-700',
                            sms.deliveryStatus === 'SENT' && 'bg-yellow-100 text-yellow-700',
                            sms.deliveryStatus === 'FAILED' && 'bg-red-100 text-red-700',
                          )}>
                            {sms.deliveryStatus}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {smsHistoryTotalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Page {smsHistoryPage} / {smsHistoryTotalPages} ({filteredSmsHistory.length} SMS)
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setSmsHistoryPage(p => Math.max(1, p - 1))}
                  disabled={smsHistoryPage <= 1}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Précédent
                </button>
                <button
                  onClick={() => setSmsHistoryPage(p => Math.min(smsHistoryTotalPages, p + 1))}
                  disabled={smsHistoryPage >= smsHistoryTotalPages}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'healthcheck' && <HealthCheckTab />}
    </div>
  );
}

// ============================================================================
// HEALTH CHECK TAB (daily diagnostic report)
// ============================================================================

interface HCCheckResult {
  name: string;
  severity: 'OK' | 'WARNING' | 'CRITICAL';
  message: string;
  value?: string;
}

interface HCTrackerIssue {
  tenantName: string;
  deviceName: string;
  plate: string;
  imei: string;
  severity: 'OK' | 'WARNING' | 'CRITICAL';
  problem: string;
  lastComm: string;
}

interface HCReport {
  timestamp: string;
  durationMs: number;
  globalSeverity: 'OK' | 'WARNING' | 'CRITICAL';
  infrastructure: HCCheckResult[];
  services: HCCheckResult[];
  trackerIssues: HCTrackerIssue[];
  dataQuality: HCCheckResult[];
  metrics: {
    totalTenants: number;
    totalUsers: number;
    positions24h: number;
    activeAlerts: number;
    activeDevices: number;
  };
}

function hcSeverityIcon(s: string) {
  if (s === 'OK') return '🟢';
  if (s === 'WARNING') return '🟡';
  return '🔴';
}

function hcSeverityColor(s: string) {
  if (s === 'OK') return 'text-green-600';
  if (s === 'WARNING') return 'text-amber-500';
  return 'text-red-600';
}

function hcBannerClass(s: string) {
  if (s === 'OK') return 'bg-green-50 border-green-200 text-green-800';
  if (s === 'WARNING') return 'bg-amber-50 border-amber-200 text-amber-800';
  return 'bg-red-50 border-red-200 text-red-800';
}

function HealthCheckTab() {
  const [report, setReport] = useState<HCReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [trackerFilter, setTrackerFilter] = useState<'all' | 'CRITICAL' | 'WARNING'>('all');
  const [trackerSearch, setTrackerSearch] = useState('');
  const [trackersOpen, setTrackersOpen] = useState(false);

  async function runDiagnostic() {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.getHealthCheck();
      setReport(res.data);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Erreur lors du diagnostic');
    } finally {
      setLoading(false);
    }
  }

  const filteredTrackers = report?.trackerIssues.filter((t) => {
    if (trackerFilter !== 'all' && t.severity !== trackerFilter) return false;
    if (trackerSearch) {
      const s = trackerSearch.toLowerCase();
      return (
        t.tenantName.toLowerCase().includes(s) ||
        t.deviceName.toLowerCase().includes(s) ||
        t.plate.toLowerCase().includes(s) ||
        t.imei.toLowerCase().includes(s) ||
        t.problem.toLowerCase().includes(s)
      );
    }
    return true;
  }) ?? [];

  return (
    <div className="space-y-4">
      {/* Run button */}
      <div className="flex items-center gap-3">
        <button
          onClick={runDiagnostic}
          disabled={loading}
          className="btn-primary flex items-center gap-2"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <HeartPulse className="h-4 w-4" />}
          {loading ? 'Diagnostic en cours...' : 'Lancer le diagnostic'}
        </button>
        {report && (
          <span className="text-xs text-gray-400">
            Dernière exécution : {new Date(report.timestamp).toLocaleString('fr-FR', { timeZone: 'Europe/Luxembourg' })} — {report.durationMs}ms
          </span>
        )}
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
      )}

      {report && (
        <div className="space-y-6">
          {/* Global status banner */}
          <div className={`p-4 border rounded-xl text-center font-semibold text-lg ${hcBannerClass(report.globalSeverity)}`}>
            {hcSeverityIcon(report.globalSeverity)} État global : {report.globalSeverity === 'OK' ? 'OK' : report.globalSeverity === 'WARNING' ? 'ATTENTION' : 'CRITIQUE'}
          </div>

          {/* Metrics cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Tenants', value: report.metrics.totalTenants },
              { label: 'Utilisateurs', value: report.metrics.totalUsers },
              { label: 'Devices actifs', value: report.metrics.activeDevices },
              { label: 'Positions 24h', value: report.metrics.positions24h.toLocaleString('fr-FR') },
            ].map((m) => (
              <div key={m.label} className="bg-white border border-gray-200 rounded-xl p-4 text-center">
                <div className="text-2xl font-bold text-gray-900">{m.value}</div>
                <div className="text-xs text-gray-500 mt-1">{m.label}</div>
              </div>
            ))}
          </div>

          {/* Infrastructure */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Infrastructure</h3>
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <tbody>
                  {report.infrastructure.map((c) => (
                    <tr key={c.name} className="border-b border-gray-100 last:border-0">
                      <td className="px-4 py-2.5 w-8">{hcSeverityIcon(c.severity)}</td>
                      <td className="px-4 py-2.5 font-medium text-gray-800">{c.name}</td>
                      <td className={`px-4 py-2.5 ${hcSeverityColor(c.severity)}`}>{c.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Services applicatifs</h3>
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <tbody>
                  {report.services.map((c) => (
                    <tr key={c.name} className="border-b border-gray-100 last:border-0">
                      <td className="px-4 py-2.5 w-8">{hcSeverityIcon(c.severity)}</td>
                      <td className="px-4 py-2.5 font-medium text-gray-800">{c.name}</td>
                      <td className={`px-4 py-2.5 ${hcSeverityColor(c.severity)}`}>{c.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Tracker issues */}
          <div>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <button
                onClick={() => setTrackersOpen((v) => !v)}
                className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 hover:text-gray-900"
              >
                <ChevronDown className={clsx('h-4 w-4 transition-transform', !trackersOpen && '-rotate-90')} />
                Trackers problématiques
                <span className="ml-1 text-xs font-normal text-gray-400">
                  (informatif — alertes côté client ; {report.trackerIssues.filter((t) => t.severity === 'CRITICAL').length} critiques, {report.trackerIssues.filter((t) => t.severity === 'WARNING').length} warnings)
                </span>
              </button>
              {trackersOpen && (
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Rechercher..."
                      value={trackerSearch}
                      onChange={(e) => setTrackerSearch(e.target.value)}
                      className="pl-8 pr-3 py-1.5 text-xs border border-gray-300 rounded-lg w-48"
                    />
                  </div>
                  <select
                    value={trackerFilter}
                    onChange={(e) => setTrackerFilter(e.target.value as typeof trackerFilter)}
                    className="text-xs border border-gray-300 rounded-lg px-2 py-1.5"
                  >
                    <option value="all">Tous ({report.trackerIssues.length})</option>
                    <option value="CRITICAL">Critiques ({report.trackerIssues.filter((t) => t.severity === 'CRITICAL').length})</option>
                    <option value="WARNING">Warnings ({report.trackerIssues.filter((t) => t.severity === 'WARNING').length})</option>
                  </select>
                </div>
              )}
            </div>
            {trackersOpen && (
              filteredTrackers.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-xl p-6 text-center text-sm text-gray-500">
                {report.trackerIssues.length === 0 ? '🟢 Tous les trackers actifs fonctionnent normalement.' : 'Aucun résultat pour ce filtre.'}
              </div>
            ) : (
              <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 text-gray-500 text-left">
                      <th className="px-3 py-2 font-medium">Client</th>
                      <th className="px-3 py-2 font-medium">Véhicule</th>
                      <th className="px-3 py-2 font-medium">Plaque</th>
                      <th className="px-3 py-2 font-medium">IMEI</th>
                      <th className="px-3 py-2 font-medium">Sévérité</th>
                      <th className="px-3 py-2 font-medium">Problème</th>
                      <th className="px-3 py-2 font-medium">Dernière comm</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTrackers.map((t, i) => (
                      <tr key={`${t.imei}-${t.problem}-${i}`} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-800">{t.tenantName}</td>
                        <td className="px-3 py-2 text-gray-800">{t.deviceName}</td>
                        <td className="px-3 py-2 font-mono text-gray-600">{t.plate}</td>
                        <td className="px-3 py-2 font-mono text-gray-600">{t.imei}</td>
                        <td className={`px-3 py-2 font-medium ${hcSeverityColor(t.severity)}`}>
                          {hcSeverityIcon(t.severity)} {t.severity === 'CRITICAL' ? 'Critique' : 'Attention'}
                        </td>
                        <td className="px-3 py-2 text-gray-700">{t.problem}</td>
                        <td className="px-3 py-2 text-gray-500">{t.lastComm}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )
            )}
          </div>

          {/* Data quality */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Qualité des données</h3>
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <tbody>
                  {report.dataQuality.map((c) => (
                    <tr key={c.name} className="border-b border-gray-100 last:border-0">
                      <td className="px-4 py-2.5 w-8">{hcSeverityIcon(c.severity)}</td>
                      <td className="px-4 py-2.5 font-medium text-gray-800">{c.name}</td>
                      <td className={`px-4 py-2.5 ${hcSeverityColor(c.severity)}`}>{c.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
